import S3 from 'aws-sdk/clients/s3';
import { gql, Resolver } from '@apollo/client';

import { generateUuidFileKeyForS3 } from '@trqk/utils';

import { uploadFile } from './helpers';

/**
 * @typedef {UploadToS3Args}
 * @property {string} bucketName the S3 bucket to upload to
 * @property {string} bucketRegion the AWS region where the bucket is located
 * @property {string} identityPoolId the Cognito Identity Pool ID for the user pool; used to provide
 * unauthenticated uploads
 * @property {File | S3UploadFile} file the file to upload
 * @property {string} key optional key for the file, used to give it a location in the S3 bucket. If
 * no key is provided, the resolver checks for a `generateS3Key` in context. If none is found, it
 * generates a UUID-based key.
 * @property {string} taskId a unique ID for the associated task in the TaskManagerMachine
 *
 */
interface UploadToS3Args {
    bucketName: string;
    bucketRegion: string;
    identityPoolId: string;
    file: File | S3UploadFile;
    key?: string;
    taskId?: string;
}

/**
 * @typedef {UploadToS3Context}
 * @property {function} handleProgress a callback invoked with the current upload progress
 * @property {function} generateS3Key optional function to generate an S3 key for the file. This is
 * only used if a `key` arg is not provided. If there is no `key` arg and no `generateS3Key` is
 * provided, a UUID-based key is generated.
 * @property {function} bindManagedUpload a callback to give access to the S3 ManagedUpload instance
 * to the calling function
 */
interface UploadToS3Context {
    handleProgress?(currentProgress: S3UploadProgress): void;
    generateS3Key?: GenerateS3Key;
    bindManagedUpload?(managedUpload: S3.ManagedUpload): void;
}

/**
 * Uploads a file to an S3 bucket.
 * @param obj
 * @param args {UploadToS3Args}
 * @param context {UploadToS3Context}
 */
export const uploadToS3: Resolver = async (
    obj: Record<string, unknown>,
    args: UploadToS3Args,
    context: UploadToS3Context,
): Promise<UploadToS3Result> => {
    const { bucketName, bucketRegion, file, identityPoolId, key } = args;
    const {
        handleProgress,
        // If the context does not have a keyGenerator, use uuid() to generate a key.
        generateS3Key = generateUuidFileKeyForS3,
        bindManagedUpload,
    } = context;
    const taskId = args?.taskId ?? null;

    try {
        return await uploadFile({
            bindManagedUpload,
            bucketName,
            bucketRegion,
            file,
            generateS3Key,
            handleProgress,
            identityPoolId,
            key,
            taskId,
        });
    } catch (err) {
        const isRequestAbortedError = err
            .toString()
            .includes('RequestAbortedError');

        if (isRequestAbortedError) {
            return {
                aborted: true,
                completed: false,
                error: null,
                result: null,
                taskId,
            };
        }

        return {
            aborted: false,
            completed: false,
            error: err,
            result: null,
            taskId,
        };
    }
};

export const UPLOAD_TO_S3 = gql`
    mutation UploadToS3(
        $bucketName: String
        $bucketRegion: String
        $identityPoolId: String
        $file: Upload
        $key: String
        $taskId: ID
    ) {
        uploadToS3(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            identityPoolId: $identityPoolId
            file: $file
            key: $key
            taskId: $taskId
        ) @client
    }
`;
